<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template>
	<div class="k-main-wrapper">
		<div class="k-main-wrapper-bgd"></div>
		<div v-show="sparkl_embed_banner_hidden" class="k-sparkl-embed-show-banner-btn elevation-3" role="button" tabindex="0" aria-label="Show activity banner" @click.stop="sparkl_deminimize_header" @keydown.enter.stop.prevent="sparkl_deminimize_header" @keydown.space.stop.prevent="sparkl_deminimize_header"><k-icon color="#000">fas fa-arrow-down</k-icon></div>
		<k-app-bar v-show="!sparkl_embed_banner_hidden" app dense :hide-on-scroll="small_screen" class="k-app-toolbar" :class="current_sparkl_embed_component?'k-app-toolbar-sparkl-showing':'k-app-toolbar-sparkl-not-showing'" role="banner">
			<img
				v-if="agency_logo_img_src"
				@click="logo_clicked"
				@keydown.enter.prevent="logo_clicked"
				@keydown.space.prevent="logo_clicked"
				class="k-toolbar__logo-img"
				:alt="site_config.agency_name"
				:src="agency_logo_img_src"
				role="button"
				tabindex="0"
				:aria-label="home_banner_label"
			>
			<span
				v-if="site_config.banner_logo_text"
				@click="logo_clicked"
				@keydown.enter.prevent="logo_clicked"
				@keydown.space.prevent="logo_clicked"
				class="k-cureum-logo-text"
				v-html="site_config.banner_logo_text"
				translate="no"
				:role="agency_logo_img_src ? undefined : 'button'"
				:tabindex="agency_logo_img_src ? undefined : 0"
				:aria-label="agency_logo_img_src ? undefined : home_banner_label"
			></span>
			<!-- if viewing a subdomain, show agency name and btn to return to state-wide view -->
			<span v-if="subdomain_data" @click="logo_clicked" @keydown.enter.prevent="logo_clicked" @keydown.space.prevent="logo_clicked" class="k-cureum-subdomain-text" translate="no" role="button" tabindex="0" :aria-label="`Go to ${site_config.app_name} home`">
				<span v-html="subdomain_data.agency_name"></span>
				<k-tooltip bottom><template v-slot:activator="{on}">
					<k-btn v-on="on" v-if="subdomain_data" icon small class="elevation-1 ml-1" color="#fff" style="margin-top:-3px" accessible-label="Return to statewide view" @click.stop="cancel_subdomain"><k-icon>fas fa-circle-xmark</k-icon></k-btn>
				</template>Show state-wide version of {{site_config.app_name}}</k-tooltip>
			</span>
			<k-spacer/>

			<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" v-if="alt_role_label" color="pink accent-4" class="mr-3 k-tight-btn k-nocaps-btn" dark @click="return_to_default_role"><k-icon small class="mr-2">fas fa-glasses</k-icon>{{alt_role_label}} <k-icon small class="ml-2">fas fa-arrow-rotate-left</k-icon></k-btn></template>Click to return to default role</k-tooltip>

			<k-btn v-if="show_daily_lessons" class="mr-6 k-tight-btn k-nocaps-btn elevation-0" style="background-color:#E3F2FD" color="blue darken-4" outlined @click="show_daily_lessons_clicked">Daily Lesson Plans<k-icon small class="ml-2">fas fa-flag-checkered</k-icon></k-btn>
			<!-- Sparkl btns -->
			<div v-show="current_sparkl_embed_component && viewing_original_of_in_my_collections" style="font-size:12px; border-radius:4px; background-color:rgba(255,255,255,0.8); padding:2px 6px;" class="mr-2 green--text text--darken-3">
				<k-icon color="green darken-3" small class="mr-1" style="margin-top:-3px;">fas fa-cubes-stacked</k-icon>
				<i>You are viewing the original version of an activity you copied for your use</i>
				<k-btn x-small text color="green darken-3" class="ml-1" @click="show_copy_of_original_sparkl">Show My Copy</k-btn>
			</div>
			<div v-show="current_sparkl_embed_component && viewing_copy_of_original" style="font-size:12px; border-radius:4px; background-color:rgba(255,255,255,0.8); padding:2px 6px;" class="mr-2 green--text text--darken-3">
				<k-icon color="green darken-3" small class="mr-1" style="margin-top:-3px;">fas fa-cubes-stacked</k-icon>
				<i>This activity has been copied for your use</i>
				<k-btn x-small text color="green darken-3" class="ml-1" @click="show_original_sparkl">Show Original</k-btn>
			</div>
			<!-- the following button was removed on 1/16/2026 -->
			<!-- <k-btn v-show="current_sparkl_embed_component && !viewing_original_of_in_my_collections&&show_copy_for_my_use_btn" class="k-tight-btn" color="green darken-2" dark @click.stop="copy_to_my_content($event)"><k-icon small class="mr-2">fas fa-cubes-stacked</k-icon>{{copy_to_my_content_btn_text}}</k-btn> -->
			<k-btn v-if="current_sparkl_embed_component && show_sparkl_close_btn" class="ml-2 k-tight-btn" color="amber" accessible-label="Close activity" @click.stop="sparkl_close_btn_clicked"><k-icon small class="mr-2">fas fa-times-circle</k-icon>{{ close_activity_btn_text }}</k-btn>
			<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="current_sparkl_embed_component && show_sparkl_close_btn" v-on="on" text small color="amber" class="ml-2" accessible-label="Minimize banner" @click="sparkl_minimize_header"><k-icon>fas fa-arrow-up</k-icon></k-btn></template><span>Minimize banner</span></k-tooltip>
			<!-- end of sparkl btns -->

			<div v-show="!current_sparkl_embed_component" v-if="!small_screen" class="k-toolbar__username" translate="no">{{user_info.first_name}} {{user_info.last_name}}</div>
			<div v-show="site_config.show_signin_on_load == 'yes' && !signed_in" v-if="!small_screen" class="k-toolbar__username" translate="no">Guest Mode</div>

			<div v-if="support_icon.enabled">
				<k-tooltip bottom>
					<template v-slot:activator="{on}">
						<k-btn v-on="on" class="k-toolbar__username mr-1" icon accessible-label="Open support" @click="support_clicked">
							<k-icon style="font-size: 28px;">{{ support_icon.icon }}</k-icon>
						</k-btn>
					</template>
					{{ support_icon.tooltip }}
				</k-tooltip>
			</div>

			<div v-if="show_pop_up_btn">
				<k-btn class="k-toolbar__username mr-2" icon accessible-label="Open announcement" @click="pop_up_logo_clicked">
					<k-icon style="font-size: 28px;">{{ pop_up_icon }}</k-icon>
				</k-btn>
			</div>

			<div v-if="show_toolbar_search">
				<k-btn class="mr-3 elevation-0" style="margin-top:0px;" fab color="#666" dark small accessible-label="Search" @click="initiate_search($event)">
					<k-icon>fas fa-search</k-icon>
				</k-btn>
			</div>

			<UserMenu v-show="!current_sparkl_embed_component && !in_reslink_view" v-if="signed_in" />
			<k-btn v-show="!current_sparkl_embed_component" v-if="!signed_in&&site_config.show_banner_create_account_btn=='yes'&&!backdoor_login_only" color="#666" class="mr-3 k-tight-btn" dark @click="create_account"><k-icon small class="mr-2">fas fa-user-plus</k-icon>Create Account</k-btn>
			<k-btn v-show="!current_sparkl_embed_component" v-if="!signed_in&&site_config.show_banner_signin_btn=='yes'&&$vuetify.breakpoint.smAndUp&&!backdoor_login_only" :color="signin_btn_color" @click="sign_in">  Sign In<k-icon small class="mx-2">fas fa-sign-in</k-icon></k-btn>
		</k-app-bar>

		<!-- see comment in route_is_welcome -->
		<div v-show="route_is_welcome" data-main-focus-start="true" tabindex="-1">
			<WelcomeView :signin_method="''" :subdomain="''" />
		</div>
		<div v-show="!route_is_welcome" data-main-focus-start="true" tabindex="-1">
			<keep-alive><router-view :key="router_key"></router-view></keep-alive>
		</div>

		<div class="k-copyright">
			{{ site_config.copyright_text }} &nbsp; (v{{$store.state.app_version}})
			<span v-if="site_config.show_terms_of_service" class="ml-6">
				<!-- <k-btn x-small text class="k-tight-btn k-nocaps-btn" color="#ccc" @click="show_terms_of_service">Terms of Use / Privacy Policy</k-btn> -->
				<a class="k-copyright__link" @click="show_terms_of_service">Terms of Use / Privacy Policy</a>
			</span>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import UserMenu from './UserMenu'
import WelcomeView from './WelcomeView'

export default {
	components: { UserMenu, WelcomeView },

	data() { return {
		sparkl_embed_banner_hidden: false,
	}},

	computed: {
		...mapState(['user_info', 'site_config', 'current_sparkl_embed_component', 'subdomain_data']),
		...mapGetters(['small_screen', 'signed_in', 'backdoor_login_only', 'show_daily_lessons']),
		
		in_reslink_view() { return vapp.in_reslink_view },

		route_is_welcome() {
			return [
				'welcome',
				'signin',
				'signin_method',
				'welcome_alt',
				'home',
				'courses',
				'courses_cat',
				'courses_subcat',
				'repos',
				'mycollections',
				'pd_main',
				'admin',
				'standards',
				'standards_framework',
				'standards_item',
				'asset_manager',
				'resource_usage',
				'lti_chooser',
				'subdomain_start',
				'support'
			].includes(this.$route.name)
			/*
				The "standards" tab needs to show satchel in an iframe if/when the user goes to that tab; but once the user has gone there,
					we don't want the iframe to ever reload. In order to do this, we have to ensure that the StandardsHomeSatchel component never gets disabled
					and in order to ensure *that*, we also have to ensure that the WelcomeView component never gets disabled.
				To accomplish this, we partially "short-circuit" the router functionality, by making it so that if the router is telling us to go to any of the "welcome" pages,
					we hide the router-view component and instead show the WelcomeView component. 
				For all other routes, we show and use the router-view component and hide the WelcomeView component.
			*/
		},

		role() { return this.user_info.role },

		signin_btn_color() { return this.site_config.banner_signin_btn_color },

		simulated_role: {
			get() { return this.$store.state.lst.simulated_role },
			set(val) { this.$store.commit('lst_set', ['simulated_role', val]) }
		},

		simulated_user: {
			get() { return this.$store.state.lst.simulated_user },
			set(val) { this.$store.commit('lst_set', ['simulated_user', val]) }
		},

		alt_role_label() {
			if (this.simulated_role) {
				if (this.role == 'student') return 'STUDENT View'
				if (this.role == 'parent') return 'GUARDIAN View'
				if (this.role == 'staff') return 'STAFF View'
			} else if (this.simulated_user) {
				return 'MIMICKING ' + this.simulated_user
			}
			return ''
		},

		main_mode() {
			let path = this.$route.fullPath
			return path.replace(/^\/(\w+).*/, '$1')
		},

		agency_logo_img_src() {
			if (!this.site_config.agency_logo) return ''
			return this.site_config.agency_logo
		},

		home_banner_label() {
			return `Go to ${this.site_config.app_name} home`
		},

		router_key() {
			let path = this.$route.fullPath
			// by using these keys, we preserve the right amount of "history" in our components...
			if (vapp.$route.name == 'collection_view') {
				return path.replace(/(\/collection\/.+?)(\/|$).*/, '$1')
			// if (vapp.$route.name == 'course_view') {
			// 	return path.replace(/(\/course\/.+?)(\/|$).*/, '$1')
			// } else if (vapp.$route.name == 'repo_course_view' || vapp.$route.name == 'repo_course_view2') {
			// 	return path.replace(/(\/repo\/.+?)(\/|$).*/, '$1')
			// } else if (vapp.$route.name == 'mycollection_course_view') {
			// 	return path.replace(/(\/mycollection\/.+?)(\/|$).*/, '$1')
			} else {
				// we only need to render the 'WelcomeView' component once; all these routes use it
				if (['welcome', 'signin', 'welcome_alt', 'home', 'courses', 'repos', 'mycollections', 'admin', 'standards'].includes(vapp.$route.name)) {
					return 'welcome'
				} else {
					return path
				}
			}
		},
		
		show_pop_up_btn() { 
			// don't show in_reslink_view
			if (this.in_reslink_view) return false
			// if we're showing a sparkl activity, don't show the pop up btn
			if (this.current_sparkl_embed_component) return false
			// show the home btn in the banner if site_config.show_pop_up is on...
			if (this.site_config.show_pop_up == true) return true
			// or if we're showing a standlone lesson
			if (this.$route.name == 'standalone_lesson_view') return true
			// or, possibly in the future, a resource?
			// if (this.$route.name == 'standalone_resource_view') return true
			return false
		},

		show_toolbar_search() { 
			// don't show in_reslink_view
			if (this.in_reslink_view) return false
			// if we're showing a sparkl activity, don't show this
			if (this.current_sparkl_embed_component) return false
			// otherwise show IFF site_config says to show it
			if (this.site_config.show_toolbar_search == true) return true

			// DEBUG
			return true
		},

		pop_up_icon() {
			return this.site_config.pop_up?.icon || 'fas fa-home'
		},

		support_icon() {
			return this.site_config.support_icon
		},

		// from SparklEmbed
		viewing_original_of_in_my_collections() { return this.current_sparkl_embed_component?.viewing_original_of_in_my_collections },
		viewing_copy_of_original() { return this.current_sparkl_embed_component?.viewing_copy_of_original },
		show_sparkl_close_btn() { return this.current_sparkl_embed_component?.show_close_btn },
		show_copy_for_my_use_btn() { return this.current_sparkl_embed_component?.show_copy_for_my_use_btn },
		// copy_to_my_content_btn_text() { return this.current_sparkl_embed_component?.copy_to_my_content_btn_text },	// not used as of 1/16/2026
		close_activity_btn_text() { return this.current_sparkl_embed_component?.close_activity_btn_text },
	},

	watch: {
		'$route.path': {immediate: true, handler(val) {
			// console.warn('MainView route watcher: route ' + this.$route.path)
		}},
	},

	created() {
		window.main_view = this
	},
	
	methods: {
		// from SparklEmbed
		show_original_sparkl() { this.current_sparkl_embed_component.show_original_sparkl() },
		show_copy_of_original_sparkl() { this.current_sparkl_embed_component.show_copy_of_original_sparkl() },
		copy_to_my_content($event) { this.current_sparkl_embed_component.copy_to_my_content($event, 'force_copy') },
		use_with_students() { this.current_sparkl_embed_component.use_with_students() },
		sparkl_close_btn_clicked() { this.current_sparkl_embed_component.close_btn_clicked() },
		sparkl_minimize_header() { 
			this.sparkl_embed_banner_hidden = true
			this.current_sparkl_embed_component.sparkl_embed_banner_hidden = true
		},
		sparkl_deminimize_header() {
			this.sparkl_embed_banner_hidden = false
			this.current_sparkl_embed_component.sparkl_embed_banner_hidden = false
		},

		cancel_subdomain() { vapp.cancel_subdomain() },

		logo_clicked() {
			// if a sparkl activity is showing, close it...
			if (vapp.$refs.sparkl_embed.sparkl_showing) {
				this.sparkl_close_btn_clicked()
			
			// else if we're showing the standalone lesson view, go to the home/welcome page
			} else if (this.$route.name == 'standalone_lesson_view') {
				vapp.go_to_home()

			// else go to the home page
			} else {
				this.go_to_route('home')
			}
		},

		support_clicked() {
			this.$router.push({path: '/support'})
		},

		pop_up_logo_clicked() {
			vapp.show_pop_up = true
		},

		return_to_default_role() {
			// if we're in "sandbox" mode, call simulate_sandbox_role
			if (this.site_config.enable_sandbox_simulate_role_menu == 'yes') {
				vapp.simulate_sandbox_role('')

			} else {
				// otherwise just reset simulated role/user and reload
				this.simulated_role = ''
				this.simulated_user = ''
				window.location.reload()
			}
		},

		go_to_route(new_route, event) {
			if (!empty(event) && !empty(event.target)) $(event.target).closest('button').blur()

			new_route = '/' + new_route
			if (this.$route.path == new_route) return

			this.$router.push({ path: new_route })
		},

		sign_out() {
			// for demo purposes we sign out by clearing password from local_storage and reloading
			U.local_storage_set('district_portal_login_password', '')
			window.location.reload()

			// this.$store.dispatch('sign_out')
		},

		show_change_log() {
			this.go_to_route('changelog')
		},

		cycle_bgd_image() {
			vapp.show_bgd_image(-1)
		},

		sign_in() {
			vapp.redirect_to_login()
		},

		create_account() {
			vapp.redirect_to_login('create_account')
		},

		initiate_search(event) {
			vapp.initiate_search({
				search_only: true,
				dialog_title: 'Search Site Content',
				default_source: 'site',
				focusOriginEl: event?.currentTarget || document.activeElement,
			})
		},

		show_terms_of_service() { vapp.show_terms_of_service() },
		show_daily_lessons_clicked() { vapp.daily_lessons_showing = true },
	}
}
</script>

<style lang="scss">
.k-main-wrapper {
	.k-main-wrapper-bgd {
		position: fixed;
		z-index: -1;
		left: 0;
		top: 0;
		width: 100vw;
		height: 100vh;
		background: rgba(0, 0, 0, 0.65)!important;
	}

	margin-bottom: 40px;

	.k-elevated {
		-webkit-box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12) !important;
		box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12) !important;
	}

	.k-app-toolbar {
		// height is 60; set in v-app-bar component; matches sparklsalt
		z-index: 11;
		background-color: $v-doe-light-orange!important;

		// note that this is hidden when the screen width is too small (responsive.scss)
		// set via site_config.banner_logo_css
		.k-cureum-logo-text {
			color: #ffffff;
			font-weight: bold;
			font-size: 32px;
			position: absolute;
			left: 120px;
			top: -3px;
			font-family: var(--agency-name-font-family);

			&,
			* {
				color: inherit !important;
				-webkit-text-fill-color: currentColor !important;
			}
		}

		// set via site_config.banner_subdomain_css
		.k-cureum-subdomain-text {
			font-weight: bold;
			color:#fff;
			font-size: 28px;
			cursor: pointer;
			position: absolute;
			left: 320px;
			top:2px;
		}

		.k-toolbar__logo-img {
			height: 64px;
			position: absolute;
			top: 4px;
			left: 0;
			background-color: $v-doe-light-orange;
			padding: 4px 16px 8px 12px;
			border-radius: 0 0 10px 0;

			-webkit-box-shadow: 0px 6px 5px -6px black;
			box-shadow: 0px 6px 5px -6px black;
		}

		.k-toolbar__username {
			margin-right: 10px;
			font-family: $display-font;
			color: #222;
			font-weight: 900;
			font-size: 1.4em;
			line-height: 1.0em;
			text-align: center;
		}
	}

	.k-main-division-wrapper {
		// margin-left: 140px;
		margin-top: 50px;
	}

	.k-page-wrapper {
		background-color: $page-background;
		padding: 15px 80px;
		margin: 30px auto 0 auto;
	}

	.k-page-title {
		font-size: 27px;
		line-height:32px;
		margin-bottom: 20px;
	}

	.k-main-collection {
		padding: 15px 50px;
		margin: 0;
		width: 100vw;
		background-color: $page-background;
	}

	.k-main-collection--item-showing {
		border-radius: 0 16px 16px 0;
		width: auto;
		height: 168px;
	}

	.k-main-collection-body {
		display: flex;
		flex-wrap: wrap;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		margin: 10px 10px;
		clear: both;
	}

	.k-main-collection-item {
		margin: 12px 16px;
		padding: 8px 10px;
		font-size: 16px;
		line-height: 22px;
		text-align: left;
		vertical-align: middle;

		width: 165px;
		height: 165px;
		border-radius: 25px;

		display: flex;
		justify-content: center;
		flex-direction: column;

		position: relative;
		cursor: pointer;

		-webkit-box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12);
	    box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12);

		.fas {
			font-size: 12px;
			color: #fff;
			margin-right: 4px;
		}
	}

	.k-lp-tile {
		width: 120px!important;
		height: 120px!important;
		border-radius: 24px!important;
		font-size: 14px!important;
		line-height: 18px!important;
		color: #fff;
		.k-class-tile-course-code {
			position: absolute;
			left: 0px;
			bottom: 10px;
			font-size: 9px;
			width: 100%;
			text-align: center;
		}
	}

	.k-lp-tile-long-title {
		font-size: 12px!important;
		line-height: 15px!important;
	}

	.k-lp-tile-extra-long-title {
		font-size: 10px!important;
		line-height: 13px!important;
	}

	.k-lp-tile-inactive {
		border: 3px dashed #ccc;
	}

}

.k-copyright {
	position: absolute;
	bottom: 0;
	right: 0;
	padding: 8px 12px;
	text-align: right;
	white-space: nowrap;
	font-size: .85em;
	color: rgba(255, 255, 255, 0.9);
	font-weight: bold;
	background: rgba(0, 0, 0, 0.38);
	border-radius: 12px 0 0 0;
	box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.18);

	// cursor pointer for easter egg
	// cursor: pointer;

	.k-copyright__link {
		color: #fff;
		font-size: 14px;
		text-decoration: underline;
		text-underline-offset: 2px;
	}
}


.k-fade-enter-active {
	transition: opacity .25s;
}
.k-fade-enter, .k-fade-leave-active {
	opacity: 0;
}

.k-sparkl-embed-show-banner-btn {
	position: fixed;
	top: -1px;
	right: 20px;
	height: 22px;
	width: 40px;
	text-align: center;
	cursor: pointer;
	background-color: $v-amber;
	border: 1px solid #000;
	border-radius: 0 0 6px 6px;
	z-index: 100;

	.fas {
		position: absolute;
		left: 13px;
		top: 2px;
		font-size: 16px;
	}
}
</style>
